import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Logo } from '@/components/ui/Logo/Logo';
import { startOAuth } from '@/lib/oauth';
import { authRedirectUrl } from '@/lib/supabase';
import { useResetOAuthInFlight } from '@/hooks/useResetOAuthInFlight';
import { SignupForm } from './components/SignupForm/SignupForm';
import { DuplicateEmailHelpLinks } from '@/components/auth/DuplicateEmailHelpLinks';
import { OAuthTermsNotice } from '@/components/auth/OAuthTermsNotice';
import {
  markVolunteerOAuthSignupIntent,
  clearVolunteerOAuthSignupIntent,
} from '@/constants/volunteerOAuthSignup';

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 262">
    <path fill="#4285f4" d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" />
    <path fill="#34a853" d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" />
    <path fill="#fbbc05" d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z" />
    <path fill="#eb4335" d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" />
  </svg>
);

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 814 1000">
    <path fill="currentColor" d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.9 0 663.1 0 541.8c0-207.7 135.4-317.5 269-317.5 70.8 0 129.9 46.4 174.3 46.4 42.7 0 109.6-49.5 188.9-49.5 30.5 0 110.7 2.6 167.4 79.2zm-164.8-186.1c31.5-37.8 54.3-90.8 54.3-143.9 0-7.4-.6-14.9-1.9-21.1-51.6 1.9-112.5 34.5-149.2 77.2-28.5 32.5-56.1 85.5-56.1 139.5 0 8.3 1.3 16.5 1.9 19.1 3.2.6 8.4 1.3 13.6 1.3 46.5 0 103.1-31.2 137.4-72.1z" />
  </svg>
);

const SocialButton = ({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}) => (
  <chakra.button
    type="button"
    w="full"
    h="11"
    px="4"
    display="flex"
    alignItems="center"
    justifyContent="center"
    gap="3"
    borderRadius="xl"
    border="1px solid"
    borderColor="volyx.borderLight"
    bg="white"
    color="volyx.text"
    fontSize="sm"
    fontWeight="medium"
    fontFamily="body"
    cursor={disabled ? 'not-allowed' : 'pointer'}
    opacity={disabled ? 0.55 : 1}
    _hover={disabled ? {} : { bg: 'gray.50' }}
    _focus={{ outline: 'none' }}
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
  >
    {icon}
    <span>{label}</span>
  </chakra.button>
);

function oauthUrlSuggestsDuplicateEmail(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('already been registered') ||
    lower.includes('already registered') ||
    lower.includes('user already registered') ||
    lower.includes('email address is already') ||
    lower.includes('duplicate key') ||
    lower.includes('identity already exists')
  );
}

export const VolunteerSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [oauthProvider, setOauthProvider] = useState<'google' | 'apple' | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthDuplicateEmail, setOauthDuplicateEmail] = useState(false);

  useEffect(() => {
    clearVolunteerOAuthSignupIntent();
  }, []);

  useResetOAuthInFlight(useCallback(() => setOauthProvider(null), []));

  useEffect(() => {
    const desc =
      searchParams.get('error_description') || searchParams.get('error') || '';
    if (desc && oauthUrlSuggestsDuplicateEmail(desc)) {
      setOauthDuplicateEmail(true);
      setOauthError(null);
    }
  }, [searchParams]);

  const handleGoogleSignup = async () => {
    setOauthError(null);
    setOauthDuplicateEmail(false);
    setOauthProvider('google');
    try {
      markVolunteerOAuthSignupIntent();
      await startOAuth('google', authRedirectUrl);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start Google sign up';
      setOauthError(message);
      setOauthProvider(null);
    }
  };

  const handleAppleSignup = async () => {
    setOauthError(null);
    setOauthDuplicateEmail(false);
    setOauthProvider('apple');
    try {
      markVolunteerOAuthSignupIntent();
      await startOAuth('apple', authRedirectUrl);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start Apple sign up';
      setOauthError(message);
      setOauthProvider(null);
    }
  };

  const handleSignupSuccess = () => {
    navigate('/?fromSignup=true', { replace: true });
  };

  return (
    <Flex
      minH="100vh"
      bgGradient="to-b"
      gradientFrom="gray.50"
      gradientTo="white"
      justify="center"
      align="center"
      px="4"
      py="12"
      overflowY="auto"
    >
      <Box
        w="full"
        maxW="440px"
        borderRadius="xl"
        border="1px solid"
        borderColor="volyx.borderLight"
        bg="white"
        boxShadow="lg"
        overflow="hidden"
      >
        <Box p="8">
          <Flex justify="center" mb="2">
            <Logo mb="0" />
          </Flex>
          <Text as="h1" mt="0" fontSize="2xl" fontWeight="semibold" lineHeight="snug" color="volyx.text" textAlign="center">
            Create your volunteer account
          </Text>

          <Flex direction="column" gap="3" mt="6">
            {oauthDuplicateEmail && (
              <Box
                p="3"
                bg="rgba(255,59,48,0.1)"
                color="volyx.error"
                borderRadius="lg"
                fontSize="sm"
                textAlign="center"
              >
                <Text mb="1">An account with this email address already exists.</Text>
                <DuplicateEmailHelpLinks />
              </Box>
            )}
            {oauthError && (
              <Box
                p="3"
                bg="rgba(255,59,48,0.1)"
                color="volyx.error"
                borderRadius="lg"
                fontSize="sm"
                textAlign="center"
              >
                {oauthError}
              </Box>
            )}
            <SocialButton
              icon={<GoogleIcon />}
              label={oauthProvider === 'google' ? 'Connecting to Google…' : 'Continue with Google'}
              disabled={oauthProvider !== null || loading}
              onClick={handleGoogleSignup}
            />
            <SocialButton
              icon={<AppleIcon />}
              label={oauthProvider === 'apple' ? 'Connecting to Apple…' : 'Continue with Apple'}
              disabled={oauthProvider !== null || loading}
              onClick={handleAppleSignup}
            />
            <OAuthTermsNotice />
          </Flex>

          <Flex align="center" my="6">
            <Box flex="1" h="1px" bg="volyx.borderLight" />
            <Text px="3" fontSize="sm" color="volyx.textSecondary">or</Text>
            <Box flex="1" h="1px" bg="volyx.borderLight" />
          </Flex>

          <SignupForm
            loading={loading}
            setLoading={setLoading}
            onSignupSuccess={handleSignupSuccess}
            oauthInFlight={oauthProvider !== null}
          />
        </Box>
      </Box>
    </Flex>
  );
};

